import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { CanalTV } from "@/entities/CanalTV";
import { Video } from "@/entities/Video";
import { AudioMP3 } from "@/entities/AudioMP3";
import { Tv, Play, Music, ChevronRight, Radio, Zap, Star } from "lucide-react";

export default function Home() {
  const [canales, setCanales] = useState([]);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function cargarDatos() {
      const [c, v, a] = await Promise.all([
        CanalTV.filter({ activo: true }, "orden", 4),
        Video.filter({ activo: true }, "orden", 4),
        AudioMP3.filter({ activo: true }, "orden", 4),
      ]);
      setCanales(c);
      setVideos(v);
      setAudios(a);
      setLoading(false);
    }
    cargarDatos();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <section className="relative overflow-hidden py-16 md:py-24 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-900/20 via-black to-orange-900/10" />
        <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-400 text-sm font-bold tracking-widest uppercase">Transmitiendo en Vivo</span>
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black mb-2 tracking-tight">
            <span className="text-yellow-400" style={{textShadow:"0 0 30px rgba(255,200,0,0.6)"}}>LUISMUSIC</span>
            <span className="text-white">TVS</span>
          </h1>
          <p className="text-gray-400 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
            Tu plataforma de <span className="text-yellow-400 font-semibold">TV en Vivo</span>, 
            <span className="text-yellow-400 font-semibold"> Videos</span> y 
            <span className="text-yellow-400 font-semibold"> Música MP3</span> en un solo lugar
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to={createPageUrl("TvEnVivo")}
              className="flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold px-6 py-3 rounded-full hover:scale-105 transition-transform shadow-lg shadow-yellow-500/30">
              <Tv className="w-5 h-5" /> Ver TV en Vivo
            </Link>
            <Link to={createPageUrl("Videos")}
              className="flex items-center gap-2 border border-yellow-500/50 text-yellow-400 font-bold px-6 py-3 rounded-full hover:bg-yellow-500/10 transition-colors">
              <Play className="w-5 h-5" /> Ver Videos
            </Link>
            <Link to={createPageUrl("AudiosMP3")}
              className="flex items-center gap-2 border border-gray-600 text-gray-300 font-bold px-6 py-3 rounded-full hover:bg-white/5 transition-colors">
              <Music className="w-5 h-5" /> Escuchar Música
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-yellow-500/20 bg-gray-950/50 py-6 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4 text-center">
          {[
            { icon: Tv, label: "Canales en Vivo", value: canales.length || "0", color: "text-red-400" },
            { icon: Play, label: "Videos", value: videos.length || "0", color: "text-yellow-400" },
            { icon: Music, label: "Audios MP3", value: audios.length || "0", color: "text-green-400" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1">
              <s.icon className={`w-6 h-6 ${s.color}`} />
              <div className={`text-2xl md:text-3xl font-black ${s.color}`}>{s.value}</div>
              <div className="text-gray-500 text-xs md:text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10 space-y-14">

        {/* TV en Vivo */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Tv className="w-4 h-4 text-red-400" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">TV en Vivo</h2>
              <span className="flex items-center gap-1 bg-red-500/20 text-red-400 text-xs font-bold px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
              </span>
            </div>
            <Link to={createPageUrl("TvEnVivo")} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300 transition-colors">
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-video bg-gray-900 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : canales.length === 0 ? (
            <div className="text-center py-10 text-gray-600 border border-dashed border-gray-800 rounded-xl">
              <Tv className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No hay canales disponibles aún</p>
              <Link to={createPageUrl("Admin")} className="text-yellow-400 text-sm hover:underline">Agregar canales →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {canales.map((canal) => (
                <Link key={canal.id} to={createPageUrl("TvEnVivo") + `?canal=${canal.id}`}
                  className="group relative aspect-video bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-yellow-500/50 transition-all hover:scale-105">
                  {canal.imagen_portada ? (
                    <img src={canal.imagen_portada} alt={canal.nombre} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
                      <Tv className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-bold truncate">{canal.nombre}</p>
                    <span className="text-red-400 text-xs flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> En vivo
                    </span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full bg-yellow-400/90 flex items-center justify-center">
                      <Play className="w-5 h-5 text-black ml-0.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Videos */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <Play className="w-4 h-4 text-yellow-400" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">Videos</h2>
            </div>
            <Link to={createPageUrl("Videos")} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300 transition-colors">
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-video bg-gray-900 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-10 text-gray-600 border border-dashed border-gray-800 rounded-xl">
              <Play className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No hay videos disponibles aún</p>
              <Link to={createPageUrl("Admin")} className="text-yellow-400 text-sm hover:underline">Agregar videos →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {videos.map((video) => (
                <Link key={video.id} to={createPageUrl("Videos") + `?video=${video.id}`}
                  className="group relative aspect-video bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-yellow-500/50 transition-all hover:scale-105">
                  {video.thumbnail ? (
                    <img src={video.thumbnail} alt={video.titulo} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
                      <Play className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2">
                    <p className="text-white text-xs font-bold truncate">{video.titulo}</p>
                    {video.duracion && <p className="text-gray-400 text-xs">{video.duracion}</p>}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full bg-yellow-400/90 flex items-center justify-center">
                      <Play className="w-5 h-5 text-black ml-0.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Audios MP3 */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Music className="w-4 h-4 text-green-400" />
              </div>
              <h2 className="text-xl md:text-2xl font-bold text-white">Audios MP3</h2>
            </div>
            <Link to={createPageUrl("AudiosMP3")} className="flex items-center gap-1 text-yellow-400 text-sm hover:text-yellow-300 transition-colors">
              Ver todos <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-900 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : audios.length === 0 ? (
            <div className="text-center py-10 text-gray-600 border border-dashed border-gray-800 rounded-xl">
              <Music className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No hay audios disponibles aún</p>
              <Link to={createPageUrl("Admin")} className="text-yellow-400 text-sm hover:underline">Agregar audios →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {audios.map((audio, idx) => (
                <Link key={audio.id} to={createPageUrl("AudiosMP3") + `?audio=${audio.id}`}
                  className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-800 hover:border-yellow-500/50 hover:bg-gray-800 transition-all group">
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-green-900 to-gray-800 flex items-center justify-center">
                    {audio.imagen_portada ? (
                      <img src={audio.imagen_portada} alt={audio.titulo} className="w-full h-full object-cover" />
                    ) : (
                      <Music className="w-5 h-5 text-green-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{audio.titulo}</p>
                    <p className="text-gray-500 text-xs truncate">{audio.artista || "Artista desconocido"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {audio.duracion && <span className="text-gray-600 text-xs">{audio.duracion}</span>}
                    <div className="w-8 h-8 rounded-full bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-400 transition-colors">
                      <Play className="w-3.5 h-3.5 text-yellow-400 group-hover:text-black ml-0.5" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}